// ============================================================
// LoadingSplash.tsx
// Branded post-login loading screen (approved Option A). Shows AFTER
// sign-in while the app loads into the dashboard, then fades out.
//
// Theme-aware: the same full-colour logo on both; only the canvas,
// tagline, and progress bar adapt for dark mode (via casevo-* tokens +
// a dark: bg gradient). Animations: fade-up entrance + a sliding
// progress bar. The `fading` prop drives the opacity fade-out (App
// unmounts the component once the fade completes).
//
// Rendered ONLY inside the authenticated layout (App.tsx), so it can
// never appear on the login screen / unauthenticated.
// ============================================================
import React from 'react'

interface LoadingSplashProps {
  // When true, the overlay transitions to opacity 0 (fade-out) and stops
  // capturing pointer events so the revealed dashboard is usable.
  fading?: boolean
}

const LoadingSplash: React.FC<LoadingSplashProps> = ({ fading = false }) => (
  <div
    aria-hidden="true"
    className={
      'fixed inset-0 z-[300] flex items-center justify-center transition-opacity duration-500 ' +
      'bg-white dark:bg-gradient-to-b dark:from-[#17251d] dark:to-[#0f1b15] ' +
      (fading ? 'opacity-0 pointer-events-none' : 'opacity-100')
    }
  >
    {/* Scoped keyframes (unique names so they can't collide with app CSS). */}
    <style>{`
      @keyframes casevoSplashFadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
      @keyframes casevoSplashSlide{0%{transform:translateX(-100%)}100%{transform:translateX(320%)}}
    `}</style>
    <div className="flex flex-col items-center px-6" style={{ animation: 'casevoSplashFadeUp 1s ease both' }}>
      {/* Full-colour logo mark — the hero. Viewport-relative so it fills a
          substantial central portion of the screen on any size (min 180px
          on small screens, up to 300px on large monitors), never edge-to-
          edge. Same colour in light + dark. */}
      <svg
        viewBox="0 0 512 512"
        role="img"
        aria-label="Casevo"
        className="h-auto"
        style={{ width: 'clamp(140px, 26vw, 240px)' }}
      >
        <circle cx="256" cy="256" r="256" fill="#2d6a4f" />
        <rect x="176" y="128" width="60" height="256" rx="30" fill="#ffffff" />
        <rect x="284" y="182" width="60" height="166" rx="30" fill="#74c69d" />
      </svg>
      {/* Tagline — scaled up + a touch more tracking to balance the logo. */}
      <p
        className="font-medium uppercase text-casevo-text-soft"
        style={{ marginTop: 'clamp(20px, 3vh, 36px)', fontSize: 'clamp(11px, 1.3vw, 14px)', letterSpacing: '3.5px' }}
      >
        Case Management
      </p>
      {/* Progress bar — wider + slightly thicker to match the bigger logo. */}
      <div
        className="rounded-full overflow-hidden bg-casevo-mint"
        style={{ marginTop: 'clamp(28px, 4vh, 42px)', width: 'clamp(170px, 24vw, 250px)', height: '4px' }}
      >
        <div
          className="h-full w-[40%] rounded-full bg-casevo-brand"
          style={{ animation: 'casevoSplashSlide 1.4s ease-in-out infinite' }}
        />
      </div>
    </div>
  </div>
)

export default LoadingSplash
